import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Observable, catchError, forkJoin, lastValueFrom, map } from 'rxjs';
import { BaseOperation } from 'src/app/perical/models/thermal-operation.model';
import { ThermalOperationsService } from 'src/app/perical/services/thermal-operations.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { Apb, ApbAnnulus, ApbInputUi, FinalOperation } from '../../models/apb.model';
import { ApbService } from '../../services/apb.service';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { PoreAndFracService } from 'src/app/perivis/services/pore-and-frac.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { LinearInterpSingle } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';

@Component({
    selector: 'app-apb-inputs',
    templateUrl: './apb-inputs.component.html',
    standalone: false
})
export class ApbInputsComponent implements OnInit, OnDestroy {
  public apbData = signal<Apb>(undefined);
  public thermalOperations = signal<Partial<BaseOperation>[]>([]);
  public initialTemperatureOperations = signal<Array<{ id: string | null; name: string }>>([
    { id: null, name: 'Undisturbed' }
  ]);
  public volumeUnitLabel: string;
  public lengthUnitLabel: string;
  public pressureUnitLabel: string;
  public densityUnitLabel: string;
  public leakOffDepthValidation: { min: number, max: number };

  private _fracGradients: any[];
  private _userUnits: UserUnitsModel;
  private _shortLengthUnit: string;
  private _wellConfig: any[];
  private _needsUpdate: boolean;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.apbInputsStore = this._storeService.createStore(this.componentId, new ApbInputUi);
    this.selectedAnnulus.set(this.apbInputsStore.state.selectedAnnulus);
  }
  get componentId(): string {
    return this._componentId;
  }
  public apbInputsStore: Store;

  public selectedAnnulus = signal<ApbAnnulus>(undefined);

  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _apbService: ApbService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _messenger: MediatorService,
    private _wellConfigService: WellConfigService,
    private _poreAndFracService: PoreAndFracService,
    private _trajectoryService: TrajectoryService,
    private _packerService: PackerService
  ) { }

  async ngOnInit(): Promise<void> {
    this._userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.volumeUnitLabel = this._userUnits.volume;
    this.lengthUnitLabel = this._userUnits.longLengths;
    this.pressureUnitLabel = this._userUnits.pressure;
    this.densityUnitLabel = unitsLib[this._userUnits.density].symbol;
    this._shortLengthUnit = this._userUnits.shortLengths;
    this.getData();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private updateDefaultValues(apb: Apb, wellConfig: any): void {
    apb.apbAnnuli.forEach(async (annulus: ApbAnnulus) => {
      this._needsUpdate = false;
      const tubular = wellConfig.find(t => t.id === annulus.tubularStringId);
      if (annulus.maxAllowedHangerPressure == null || (annulus.maxAllowedHangerPressure == 0 && annulus.isVented == false && tubular.hangerPressure > 0)) {
        annulus.maxAllowedHangerPressure = tubular.hangerPressure;
        annulus.tubularStringId = tubular.id;
        this._needsUpdate = true;
      }
      if (annulus.leakOffDepth == null || annulus.leakOffDepth == 0) {
        const packers = await lastValueFrom(this._packerService.getPackersForTubular(tubular.id));
        const packerDepths = tubular.type == 'Tubing' ? packers.packers.map(p => p.measuredDepth) : [];
        const previousShoeMd = this._wellConfig[tubular.sequenceNumber - 1]?.shoeMd || 0;
        const minDepth = Math.min(previousShoeMd, tubular.topOfCementMd, ...packerDepths);

        if (annulus.leakOffDepth == minDepth) {
          if (annulus.leakOffDensity == null || annulus.leakOffDensity == 0) {
            annulus.leakOffDensity = await this.getFractureGradientAtDepth(annulus.leakOffDepth);
            annulus.tubularStringId = tubular.id;
            this._needsUpdate = true;
          }

          return;
        }

        annulus.leakOffDepth = minDepth;
        annulus.tubularStringId = tubular.id;
        this._needsUpdate = true;
      }
      if (annulus.leakOffDensity == null || annulus.leakOffDensity == 0) {
        annulus.leakOffDensity = await this.getFractureGradientAtDepth(annulus.leakOffDepth);
        annulus.tubularStringId = tubular.id;
        this._needsUpdate = true;
      }
      if (annulus.name == '') {
        const shortLengthUnit = this._shortLengthUnit === 'in' ? '"' : this._shortLengthUnit;
        annulus.name = tubular.stringSections[0].pipe.outsideDiameter + ' ' + shortLengthUnit + ' ' + tubular.type;
        annulus.tubularStringId = tubular.id;
        annulus.updateName = true;
        this._needsUpdate = true;
      }
      if (this._needsUpdate) {
        this.updateAnnulus(annulus);
      }
    });
    if (this._needsUpdate) {
      this.getData();
      this._messenger.publish(new DataSavedMessage('APB Inputs'));
      this._needsUpdate = false;
    }
  }

  private async getData(): Promise<void> {
    this._wellConfigService.getTubulars().subscribe({
      next: (wellConfig) => {
        if (wellConfig.length > 0) {

          const sources = [
            this._thermalOperationsService.getApbFinalConditionOperations() as Observable<Partial<BaseOperation>[]>,
            this._apbService.getApb() as Observable<any>,
            this._poreAndFracService.getFractureGradient() as Observable<any>
          ];

          forkJoin(sources).pipe(
            map(([thermalOperations, apb, fractureGradientData]) => {
              this._fracGradients = fractureGradientData;
              this._wellConfig = wellConfig;

              if (apb.apbAnnuli.some(a =>
                (a.maxAllowedHangerPressure == null || (a.maxAllowedHangerPressure == 0 && a.isVented == false && wellConfig.find(t => t.id === a.tubularStringId)?.hangerPressure > 0)) ||
                a.leakOffDensity == null || a.leakOffDensity == 0 || a.name == '' || a.name == null)) {
                  this.updateDefaultValues(apb, wellConfig);
              }

              const currentString = wellConfig.find(t => t.id === this.selectedAnnulus()?.tubularStringId) || wellConfig[0];
              this.leakOffDepthValidation = { min: +currentString.hangerMd.toFixed(3), max: +currentString.shoeMd.toFixed(3) };

              this.apbData.set(apb);
              if (!this.selectedAnnulus()?.tubularStringId) {
                this.selectedAnnulus.set(this.apbData().apbAnnuli[0]);
                this.apbInputsStore.update(state => ({ ...state, selectedAnnulus: this.selectedAnnulus() }));
              }
              this.thermalOperations.set(thermalOperations);
              const operationOptions = (thermalOperations as Partial<BaseOperation>[])
                .filter((operation): operation is Partial<BaseOperation> & { id: string; name: string } =>
                  !!operation.id && !!operation.name &&
                  operation.id !== 'UDT' && operation.name.toLowerCase() !== 'undisturbed')
                .map(operation => ({ id: operation.id, name: operation.name }));
              this.initialTemperatureOperations.set([
                { id: null, name: 'Undisturbed' },
                ...operationOptions
              ]);
              this.onAnnulusSelected(apb?.apbAnnuli.find(a => a.tubularStringId === this.selectedAnnulus()?.tubularStringId) || apb?.apbAnnuli[0]);
            }),
            catchError(err => {
              return err;
          })).subscribe();
      }}
    });
  }

  private async signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_APB ||
      data.action == PeriforOnChangeMessages.REFRESH_TUBULARS_LIST) {
      this.getData();
    }
  }

  private async getFractureGradientAtDepth(depth: number): Promise<number> {
    if (!this._fracGradients || this._fracGradients.length === 0) {
      return 0;
    }
    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([depth]));
    const fracGradient = +LinearInterpSingle(tvds[0], this._fracGradients, this._userUnits);
    return fracGradient;
  }

  public onAnnulusSelected(e): void {
    if (!e) { 
      return;
    }
    const currentString = this._wellConfig.find(t => t.id === e.tubularStringId);
    
    this.selectedAnnulus.set(e);
    this.apbInputsStore.update(state => ({ ...state, selectedAnnulus: e }));
    this.leakOffDepthValidation = { min: currentString.hangerMd, max: currentString.shoeMd };
  }

  async updateAnnulus(annulus: ApbAnnulus, isLeakoOffDepth?: boolean, isLeakOffDensity?: boolean): Promise<void> {
    if (annulus.maxAllowedHangerPressure == null) {
      const tubular = this._wellConfig.find(t => t.id === annulus.tubularStringId);
      annulus.maxAllowedHangerPressure = tubular.hangerPressure;
    }

    if (annulus.leakOffDepth != null && (isLeakoOffDepth || (isLeakOffDensity && annulus.leakOffDensity == null))) {
      annulus.leakOffDensity = await this.getFractureGradientAtDepth(annulus.leakOffDepth);
    }

    if (isLeakoOffDepth && annulus.leakOffDepth == null) {
      const tubular = this._wellConfig.find(t => t.id === annulus.tubularStringId);
      annulus.leakOffDepth = tubular.hangerMd;
      annulus.leakOffDensity = await this.getFractureGradientAtDepth(annulus.leakOffDepth);
    }

    await lastValueFrom(this._apbService.editAnnulus(annulus));
    this._messenger.publish(new DataSavedMessage('APB Inputs'));
  }

  async updateFinalConditions(finalOperation: FinalOperation): Promise<void> {
    await lastValueFrom(this._apbService.changeFinalOperation(finalOperation.id));
    this._messenger.publish(new DataSavedMessage('APB Inputs'));
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
